/**
 * Coronis rich error model (#4265 follow-up — "make errors more informative").
 *
 * `@aegle/ra-commons`' `AegleApiError` is intentionally minimal: it keeps
 * `status`/`message`/`category`/`fields`/`correlationId` and DROPS the extra
 * structure our mgmt-api sends. Coronis backends return actionable, structured
 * errors, e.g. the read-only partner guard:
 *
 *   { "error": "Partner is read-only",
 *     "details": "Write operations are not permitted for this partner. Select a writable partner to continue.",
 *     "code": "PARTNER_READ_ONLY" }
 *
 * Collapsing that to a single line loses the actionable `details` and the
 * support-friendly `code`. This module recovers them and formats a multi-line
 * notification so the user sees the *why* and the *what next*, not just a title.
 *
 * We still lean on ra-commons `parseApiError` for status/message/category/
 * correlationId so the normalization stays shared; we only layer the extra
 * fields Coronis needs on top.
 */
import { parseApiError, isAegleApiError, type AegleErrorCategory } from "@aegle/ra-commons";

export type NotifySeverity = "warning" | "error";

export interface RichError {
  status: number;
  /** Primary title, e.g. "Partner is read-only". */
  message: string;
  /** Actionable explanation, e.g. "Select a writable partner to continue." */
  details?: string;
  /** Machine code for support/telemetry, e.g. "PARTNER_READ_ONLY". */
  code?: string;
  /** Backend correlation id for tracing, when present. */
  correlationId?: string;
  category: AegleErrorCategory;
  severity: NotifySeverity;
}

// Auth/validation/not-found/conflict are user-actionable → warning; server/
// network/unknown are failures the user can't fix → error. Mirrors ra-commons.
const SEVERITY_BY_CATEGORY: Record<AegleErrorCategory, NotifySeverity> = {
  auth: "warning",
  validation: "warning",
  not_found: "warning",
  conflict: "warning",
  server: "error",
  network: "error",
  unknown: "error",
};

const readString = (obj: unknown, key: string): string | undefined => {
  if (!obj || typeof obj !== "object") return undefined;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
};

/**
 * Normalize any error into a {@link RichError}, recovering the `details`/`code`
 * that `AegleApiError` omits.
 *
 * Handles both input shapes we see:
 *  - the data provider hands us the normalized `HttpError` (its `.body` is an
 *    enriched `AegleApiError` carrying `details`/`code`);
 *  - `createCheckError`'s `on403` hands us a bare `AegleApiError` whose `.raw`
 *    is that normalized `HttpError` (so the enriched body is under `.raw.body`).
 * We scan the likely body locations for `details`/`code` to cover both, plus
 * the untouched backend JSON preserved under `.raw` as a last resort.
 */
export const parseRichError = (error: unknown): RichError => {
  const base = isAegleApiError(error) ? error : parseApiError(error);

  const e = error as { body?: { raw?: { body?: unknown } }; raw?: { body?: unknown } };
  const bodies: unknown[] = [e?.body, e?.raw?.body, e?.body?.raw?.body];
  const pick = (key: string): string | undefined => {
    for (const b of bodies) {
      const v = readString(b, key);
      if (v) return v;
    }
    return undefined;
  };

  // ra-commons categorizes 403 as "unknown" (→ error/red). A 403 is a permission
  // denial — treat it as auth (→ warning), consistent with 401/407. This also keeps
  // it identical to the authProvider's on403 toast so the two collapse via dedup
  // instead of showing as two differently-colored toasts.
  const category = base.status === 403 ? "auth" : base.category;

  return {
    status: base.status,
    message: base.message,
    details: pick("details"),
    code: pick("code"),
    correlationId: base.correlationId,
    category,
    severity: SEVERITY_BY_CATEGORY[category] ?? "error",
  };
};

/**
 * Multi-line notification text: title, then the actionable detail, then a
 * dim reference line (`CODE · ref <correlationId>`) for support. Requires the
 * notification to be shown with `{ multiLine: true }`.
 */
export const formatRichErrorMessage = (e: RichError): string => {
  const lines = [e.message || "An error occurred"];
  if (e.details && e.details !== e.message) lines.push(e.details);
  const ref = [e.code, e.correlationId ? `ref ${e.correlationId}` : undefined].filter(Boolean).join(" · ");
  if (ref) lines.push(ref);
  return lines.join("\n");
};
